import React, { Ref } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  CardProps,
  CardHeaderProps,
  CardContentProps
} from "@mui/material";

// project imports
import { KeyedObject } from "types";

// constant
const headerSX = {
  "& .MuiCardHeader-action": { mr: 0 }
};

// ==============================|| CUSTOM MAIN CARD ||============================== //

export interface MainCardProps extends KeyedObject {
  border?: boolean;
  padding?: boolean;
  boxShadow?: boolean;
  backgroundColor?: boolean;
  children: React.ReactNode | string;
  style?: React.CSSProperties;
  content?: boolean;
  className?: string;
  contentClass?: string;
  mainClass?: string;
  contentSX?: CardContentProps["sx"];
  darkTitle?: boolean;
  sx?: CardProps["sx"];
  secondary?: CardHeaderProps["action"];
  shadow?: string;
  elevation?: number;
  title?: React.ReactNode | string;
  subheader?: string;
}

const MainCard = React.forwardRef(
  (
    {
      border = false,
      padding = false,
      boxShadow = false,
      backgroundColor = true,
      children,
      content = true,
      contentClass = "",
      mainClass = "",
      contentSX = {},
      darkTitle,
      secondary,
      subTitle,
      subheader = "",
      shadow,
      sx = {},
      title,
      ...others
    }: MainCardProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const theme = useTheme();

    return (
      <Card
        className={mainClass}
        ref={ref}
        {...others}
        sx={{
          border: border ? "1px solid" : "none",
          //padding: padding ? { xs: "0 16px", md: "8px 24px 0" } : "0",
          backgroundColor: backgroundColor
            ? theme.palette.background.paper
            : "transparent",
          borderColor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : theme.palette.primary[200] + 75,
          boxShadow: boxShadow
            ? shadow ||
              (theme.palette.mode === "dark"
                ? "0 2px 14px 0 rgb(33 150 243 / 10%)"
                : "0px 0px 20px rgba(0, 0, 0, 0.15)")
            : "inherit",
          ...sx
        }}
      >
        {/* card header and action */}
        {!darkTitle && title && (
          <CardHeader
            sx={headerSX}
            title={title}
            action={secondary}
            subheader={subheader}
          />
        )}
        {darkTitle && title && (
          <CardHeader
            sx={headerSX}
            title={<Typography variant="h3">{title}</Typography>}
            action={secondary}
            subheader={subheader}
          />
        )}

        {subTitle && (
          <Typography variant="h4" fontWeight="normal">
            {subTitle}
          </Typography>
        )}

        {/* content & header divider */}
        {title && <Divider />}

        {/* card content */}
        {content && (
          <CardContent sx={contentSX} className={contentClass}>
            {children}
          </CardContent>
        )}
        {!content && children}
      </Card>
    );
  }
);

export default MainCard;
